import React, { useState } from 'react';

const COST_PER_LETTER = 3.75;
const CONVERSION_RATE = 0.02;
const SCAN_RATE = 0.16;

interface MetricLabel {
  key: string;
  label: string;
}

interface MailingRoiCalculatorProps {
  lettersLabel: string;
  scanRateLabel: string;
  scanRateHint: string;
  jobValueLabel: string;
  jobValuePlaceholder: string;
  metrics: readonly MetricLabel[];
  conversionNote: string;
  assumptions: string;
}

const formatNumber = (value: number, decimals = 1): string => {
  if (value % 1 === 0) return String(value);
  return value.toFixed(decimals);
};

const MailingRoiCalculator: React.FC<MailingRoiCalculatorProps> = ({
  lettersLabel,
  scanRateLabel,
  scanRateHint,
  jobValueLabel,
  jobValuePlaceholder,
  metrics,
  conversionNote,
  assumptions,
}) => {
  const [letters, setLetters] = useState(500);
  const [jobValue, setJobValue] = useState(6000);

  const qrScans = letters * SCAN_RATE;
  const customers = letters * CONVERSION_RATE;
  const monthlyLetterCost = letters * COST_PER_LETTER;
  const annualRevenue = customers * jobValue * 12;

  const metricValues: Record<string, string> = {
    scans: formatNumber(qrScans),
    customers: formatNumber(customers),
    letterCost: `€ ${Math.round(monthlyLetterCost).toLocaleString('nl-NL')}`,
    revenue: `€ ${Math.round(annualRevenue).toLocaleString('nl-NL')}`,
  };

  return (
    <div className="bg-white/60 backdrop-blur-sm rounded-2xl shadow-lg p-8 md:p-10">
      <div className="space-y-8 mb-8">
        <div>
          <label htmlFor="letters-slider" className="block text-navy font-semibold mb-4">
            {lettersLabel}
          </label>
          <input
            id="letters-slider"
            type="range"
            min={100}
            max={2000}
            step={50}
            value={letters}
            onChange={(e) => setLetters(Number(e.target.value))}
            className="w-full h-2 bg-slate/20 rounded-lg appearance-none cursor-pointer accent-sky"
          />
          <div className="text-center mt-3 text-3xl font-bold text-navy font-space">
            {letters}
          </div>
        </div>

        <div className="rounded-xl bg-sky/5 px-6 py-4 text-center">
          <p className="text-navy font-semibold mb-1">{scanRateLabel}</p>
          <p className="text-sm text-slate">{scanRateHint}</p>
        </div>

        <div>
          <label htmlFor="job-value-input" className="block text-navy font-semibold mb-4">
            {jobValueLabel}
          </label>
          <div className="relative max-w-xs mx-auto">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate font-medium">€</span>
            <input
              id="job-value-input"
              type="number"
              min={500}
              max={100000}
              step={500}
              value={jobValue}
              onChange={(e) => setJobValue(Math.max(0, Number(e.target.value) || 0))}
              placeholder={jobValuePlaceholder}
              className="w-full pl-10 pr-4 py-3 border border-slate/20 rounded-lg text-navy font-semibold text-lg focus:ring-2 focus:ring-sky focus:border-sky"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
        {metrics.map((metric) => (
          <div key={metric.key} className="text-center p-4 bg-sky/5 rounded-xl">
            <div className="text-2xl md:text-3xl font-bold text-navy font-space mb-1">
              {metricValues[metric.key]}
            </div>
            <div className="text-sm text-slate">{metric.label}</div>
          </div>
        ))}
      </div>

      <p className="text-sm text-slate font-medium text-center mb-2">{conversionNote}</p>
      <p className="text-sm text-slate/80 text-center leading-relaxed">{assumptions}</p>
    </div>
  );
};

export default MailingRoiCalculator;
